import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { SidebarProvider, SidebarInset } from '@gu-corp/gu-ui';
import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';

import { AppSidebar } from '~/components/app-sidebar';
import { MobileTabBar } from '~/components/layout/mobile-tab-bar';
import { Header } from '~/components/header';
import { ThemeProvider } from '~/components/theme-provider';
import { useAccount } from '~/hooks/with-account';
import { useDisconnect } from 'wagmi';

const MOBILE_BREAKPOINT = 768;
const SIDEBAR_STATE_KEY = 'joc-dashboard-sidebar-open';

interface LayoutProps {
  children: ReactNode;
  pageTitle?: string | ReactNode;
  pageDescription?: string | ReactNode;
  backLink?: string;
}

export function Layout({ children, pageTitle, pageDescription, backLink }: LayoutProps) {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { account } = useAccount();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mounted, setMounted] = useState(false);
  const { disconnect } = useDisconnect();

  useEffect(() => {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const storedState = localStorage.getItem(SIDEBAR_STATE_KEY);
    const initialOpen = storedState !== null ? storedState === 'true' : !isMobile;
    setSidebarOpen(initialOpen);
    setMounted(true);
  }, []);

  const handleSidebarOpenChange = useCallback((open: boolean) => {
    setSidebarOpen(open);
    localStorage.setItem(SIDEBAR_STATE_KEY, String(open));
  }, []);

  const handleLogout = useCallback(() => {
    disconnect();
    router.push('https://japanopenchain.org');
  }, [router, disconnect]);

  if (!mounted) {
    return (
      <ThemeProvider>
        <div className="flex h-screen items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={handleSidebarOpenChange}
        data-testid="layout"
      >
        <AppSidebar walletAddress={account?.ethAddress} onLogout={handleLogout} />
        <SidebarInset className="min-w-0">
          <Header walletAddress={account?.ethAddress} />
          <main className="flex-1 overflow-auto min-w-0 bg-background text-foreground">
            {(pageTitle || pageDescription || backLink) && (
              <div className="px-6 pt-6 pb-4 border-b border-border">
                {backLink && (
                  <div className="mb-2">
                    <Link
                      href={backLink}
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="inline-flex items-center gap-1">
                        <ArrowLeft className="h-4 w-4" />
                        {t('back')}
                      </span>
                    </Link>
                  </div>
                )}
                {typeof pageTitle === 'string' ? (
                  <h1 className="text-foreground text-2xl font-bold">{pageTitle}</h1>
                ) : (
                  pageTitle
                )}
                {pageDescription &&
                  (typeof pageDescription === 'string' ? (
                    <p className="text-muted-foreground text-sm pt-2">{pageDescription}</p>
                  ) : (
                    pageDescription
                  ))}
              </div>
            )}
            <div className="p-6 min-w-0 pb-20 md:pb-6">{children}</div>
          </main>
          <MobileTabBar />
        </SidebarInset>
      </SidebarProvider>
    </ThemeProvider>
  );
}

export default Layout;
